import { useContext, useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NameContext } from "../context/NameContext";
import MonoButton from "../kits/DesignKit/MonoButton";
import KompassOverview from "../kits/ManifestKit/KompassOverview";
import UnderlineSketch from "../kits/DesignKit/UnderlineSketch";
import { PaperBG } from "../kits";
import { useLocation } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { logoPath } from "../config";
import { loadModelViewer } from "../utils/loadModelViewer";


import SeoHelmet from "../components/SeoHelmet";

// RoughBorder importieren
import RoughBorder from "../kits/LayoutKit/RoughBorder";

function Hero({ className = "" }) {
  const {
    name,
    setName,
    isNameEntered,
    setIsNameEntered,
    handleNoThanks,
  } = useContext(NameContext);

  const [showModel, setShowModel] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const location = useLocation();

  // Ref für das Modal (WICHTIG für RoughBorder)
  const modalRef = useRef();

  const publicBase = process.env.PUBLIC_URL || "";
  const bypassRoutes = [
    `${publicBase}/reset-password`,
    `${publicBase}/forgot-password`,
    `${publicBase}/kompass`
  ];
  const isBypass = bypassRoutes.some(path => location.pathname.startsWith(path));

  useEffect(() => {
    if (!isNameEntered) setShowModel(true);
  }, [isNameEntered]);

  useEffect(() => {
    if (isBypass) setIsNameEntered(true);
  }, [isBypass, setIsNameEntered]);

  useEffect(() => {
    loadModelViewer();
  }, []);

  useEffect(() => {
    if (!isNameEntered && !isBypass) {
      const timeout = setTimeout(() => setShowForm(true), 50);
      return () => clearTimeout(timeout);
    }
  }, [isNameEntered, isBypass]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim() === "") setName("Demo");
    setShowModel(false);
    setTimeout(() => setIsNameEntered(true), 600);
  };

  const handleAnonymous = () => {
    handleNoThanks();
    setShowModel(false);
    setTimeout(() => setIsNameEntered(true), 600);
  };

  return (
<>

    <SeoHelmet
      title="Denis Bitter – Software Architect & Full-Stack Engineering Instructor"
      description="React · Angular · Flutter · UI Engineering · System Architecture. Ich entwickle klare Systeme für Lernen, Web & App – durchdacht, echt, skalierbar. EdTech. Medien. Lösungen mit Haltung."
      url="https://www.denisbitter.de/"
      image="https://www.denisbitter.de/images/hero-og.jpg"
    />



    <PaperBG>


      {/* Hauptbereich */}
      <section className={`
        relative font-mono text-black px-0 text-center min-h-screen      
        ${className}
        ${!isNameEntered ? "blur-sm" : ""}
      `}>
        <div className="relative z-0 max-w-2xl mx-auto flex flex-col items-center space-y-8">
          <img
            src={`${process.env.PUBLIC_URL}/images/denis.svg`}
            alt="Denis Bitter"
            className="w-full max-w-sm h-auto max-h-[250px] sm:max-h-[250px]"
          />
       
<h1 className="text-xl sm:text-2xl md:text-2xl font-light leading-snug tracking-tight">
  <span className="block sm:inline">
    <strong>Software Architect & Full-Stack Engineering Instructor</strong> <br />
     <strong>FullStack Entwickler</strong>
  </span>
  
   
  
  <br />
  <span className="text-sm block">React · Angular · Flutter · UI Engineering · System Architecture</span>

  <span className="text-xs text-gray-400 block mt-1">
    #Systemarchitektur #DigitaleBildung #FullStackEntwicklung
  </span>
</h1>


        
        </div>
        <div className="w-full flex justify-center mt-8">
          
          <HashLink smooth to="/about#kompass" scroll={el => el.scrollIntoView({ behavior: "smooth", block: "start" })}>
            <KompassOverview className="w-full max-w-4xl" 
            
            />       
               
          </HashLink>

        </div>
    <div className="max-w-2xl w-full mx-auto flex flex-col items-center justify-center">
                  <h2 className="sr-only">Werte</h2>
                  <p className="text-xs md:text-xs leading-relaxed text-center text-gray-800 font-mono">
                    Ich glaube an Systeme, die verständlich sind.
                    <br />
                    An Bildung, die bleibt. Und an Ideen, die nutzbar werden.
                    <br />
                    <br />
                    Wenn Kreativität, Technologie &amp; Haltung zusammenkommen, entsteht echte Wirkung.
                  </p>
      
              
                </div>

      </section>
     
            
       

      {/* Namens-Modal  screen */}
      {!isNameEntered && (
        <AnimatePresence mode="wait">
          <motion.div
  key="modal-wrapper"
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
  exit={{ opacity: 0 }}
  className="
    fixed inset-0 z-30 flex pointer-events-none
    justify-center items-center 
    px-4
  

    /* 📱 Mobile = Bottom Sheet */
sm:justify-center sm:pb-[calc(env(safe-area-inset-bottom)+1.5rem)] sm:items-center

    /* 💻 Desktop = Center */

        xs:items-center xs:justify-center xs:pb-20
        lg:items-center lg:justify-center lg:pb-0
        
       
    md:items-center md:justify-center md:mb-96  
    xl:items-center xl:justify-center xl:mb-96
  "
>
            <div className="relative w-11/12 max-w-md pointer-events-auto">
              {/* Hintergrund: leicht weiß + Blur */}
              <div className="absolute inset-0 bg-white backdrop-blur-sm rounded-xl z-0" />

              {/* Modal-Inhalt */}
              <motion.div
                ref={modalRef}
                key="modal"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                transition={{ duration: 0.5 }}
                className={`
                  relative z-10
                  bg-white border border-gray-900 shadow-lg rounded-xl
                  p-4 sm:p-6 space-y-6 transition-all duration-700 ease-out
                  ${showForm ? "opacity-100 scale-100" : "opacity-0 scale-95"}
                `}
              >
                {/* Logo & Puls */}
                <motion.div
                  className="relative w-14 h-14 mx-auto -mt-12 rounded-full bg-gray-50 shadow-md"
                  whileHover={{ scale: 1.05 }}
                >
                  <div className="w-full h-full flex items-center justify-center border border-gray-800 rounded-full overflow-hidden">
                    <img src={logoPath} alt="Logo" className="w-12 h-12 scale-75" loading="lazy" />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    {[...Array(2)].map((_, index) => (
                      <motion.div
                        key={index}
                        className="absolute rounded-full border border-yellow-400"
                        style={{ width: "80%", height: "80%" }}
                        initial={{ scale: 0.1, opacity: 0.5 }}
                        animate={{ scale: 1.6, opacity: 0.35 }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          repeatType: "loop",
                          delay: index * 0.6,
                        }}
                      />
                    ))}
                  </div>
                </motion.div>

                {/* Formular */}
                <h2 className="text-xs sm:text-sm text-center">
                  Moin, schön dass Du da bist. <br />Wie ist Dein Name?
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="relative">
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Mein Name ist ..."
                      className="w-full px-4 py-2 border border-black bg-white rounded-md focus:outline-none focus:ring-1 focus:ring-gray-800 text-xs text-center"
                    />
                    <div className="absolute left-0 bottom-0 w-full h-[6px] pointer-events-none">
                      <UnderlineSketch />
                    </div>
                  </div>
                  <MonoButton type="submit" className="w-full text-xs">Weiter hier</MonoButton>
                  <MonoButton
                    onClick={handleAnonymous}
                    className="w-full text-xs"
                  >
                    Nenne mich einfach Demo
                  </MonoButton>
                </form>
              </motion.div>
              {/* RoughBorder GANZ AUSSEN um das Modal */}
              <RoughBorder
                parentRef={modalRef}
                radius={18}
                padding={1}
               color="#AC8E66" 
                strokeWidth={1.2}
                roughness={2}
                bowing={2}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      )}
    
    </PaperBG>
    </>
  );

}

export default Hero;
